import { gql, useMutation } from "@apollo/client";
import { InputField } from "@src/components/fields";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import Form from "@src/components/Form";
import { CameraTypeSelect } from "@src/components/select/CameraTypeSelect";
import CountrySelect from "@src/components/select/CountrySelect";
import { basicMonthOptions, premiumMonthOptions } from "@src/components/select/options";
import {
    GQLCameraType,
    type GQLCreateCameraAdminMutation,
    type GQLCreateCameraAdminMutationVariables,
    type GQLCreateCameraInput,
} from "@src/graphql.generated";
import createDecorator from "final-form-calculate";
import { type FC, type PropsWithChildren } from "react";
import { useIntl } from "react-intl";

const createCameraMutation = gql`
    mutation CreateCameraAdmin($input: CreateCameraInput!) {
        createCameraAdmin(input: $input) {
            id
        }
    }
`;

// AG-1514: RM4eco+ cameras ship with a 3-month starter licence. Every older camera type keeps the
// previous 12 months, so the duration is a per-type default rather than a form-wide one.
const getBasicLicenceInMonths = (type: GQLCameraType): number => (type === GQLCameraType.RM4ECO_PLUS ? 3 : 12);

// initialValues only seeds the form on mount, so the duration has to be recalculated whenever the
// type select changes — otherwise switching away from RM4eco+ would keep its 3 months.
const basicLicenceByCameraType = createDecorator({
    field: "type",
    updates: {
        basicLicenceInMonths: (type: GQLCameraType) => getBasicLicenceInMonths(type),
    },
});

const AddForm: FC<PropsWithChildren> = () => {
    const intl = useIntl();
    const [createMutation] = useMutation<GQLCreateCameraAdminMutation, GQLCreateCameraAdminMutationVariables>(createCameraMutation);

    const submit = ({ hardwareInfo, ...values }: GQLCreateCameraInput) => {
        const input = Object.assign(values, {
            name: "camera",
        });

        return createMutation({
            variables: {
                input: {
                    ...input,
                    hardwareInfo,
                },
            },
        });
    };

    return (
        <Form
            mode="add"
            onSubmit={submit}
            title={intl.formatMessage({ id: "reviermanager.devices.form.add.title", defaultMessage: "Neues Gerät hinzufügen" })}
            decorators={[basicLicenceByCameraType]}
            initialValues={{
                type: GQLCameraType.RM4ECO_PLUS,
                country: "AT",
                basicLicenceInMonths: getBasicLicenceInMonths(GQLCameraType.RM4ECO_PLUS),
                premiumLicenceInMonths: 0,
            }}
        >
            <InputField name="hardwareInfo.imei" label={intl.formatMessage({ id: "reviermanager.camera.imei", defaultMessage: "IMEI" })} />
            <InputField name="hardwareInfo.sn" label={intl.formatMessage({ id: "reviermanager.camera.sn", defaultMessage: "SN" })} />
            <InputField name="hardwareInfo.msisdn" label={intl.formatMessage({ id: "reviermanager.camera.msisdn", defaultMessage: "MSISDN" })} />
            <InputField
                name="cameraId"
                type="number"
                label={intl.formatMessage({ id: "reviermanager.camera.cameraId", defaultMessage: "Kamera-ID " })}
            />
            <InputField
                type="number"
                name="activationPin"
                label={intl.formatMessage({ id: "reviermanager.camera.activationPin", defaultMessage: "Aktivierungs-PIN" })}
            />
            <StaticSelectField
                name="type"
                component={CameraTypeSelect}
                label={intl.formatMessage({ id: "reviermanager.camera.type", defaultMessage: "Typ" })}
                required
            />
            <StaticSelectField
                name="country"
                label={intl.formatMessage({ id: "reviermanager.camera.country", defaultMessage: "Land" })}
                component={CountrySelect}
                labelVariant="long"
                required
            />
            <StaticSelectField
                name="basicLicenceInMonths"
                label={intl.formatMessage({ id: "reviermanager.starter.licence.basic", defaultMessage: "Basislizenz" })}
                options={basicMonthOptions}
                searchable={false}
            />
            <StaticSelectField
                name="premiumLicenceInMonths"
                label={intl.formatMessage({ id: "reviermanager.starter.licence.premium", defaultMessage: "Premiumlizenz" })}
                options={premiumMonthOptions}
                searchable={false}
            />
        </Form>
    );
};

export default AddForm;
